import { useQuery } from '@tanstack/react-query';
import { dashboardApi, type MandalCell } from '../../api/dashboard';

interface Props {
  onCellClick: (code: string) => void;
}

function colorClasses(color: MandalCell['color']) {
  if (color === 'red')   return { bg: 'bg-red-600 text-white',         dim: 'opacity-70' };
  if (color === 'amber') return { bg: 'bg-amber-400 text-navy-900',    dim: 'opacity-80' };
  return                         { bg: 'bg-green-600 text-white',       dim: 'opacity-70' };
}

export function MandalHeatmap({ onCellClick }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'mandal-heatmap'],
    queryFn: dashboardApi.mandalHeatmap,
    refetchInterval: 60_000,
  });

  return (
    <div className="col-span-2 bg-white border border-cream-200 rounded-sm">
      <div className="px-5 py-4 border-b border-cream-100 flex items-center justify-between">
        <div>
          <h3 className="serif-display text-base text-navy-800 font-medium accent-underline inline-block">
            Mandal Health Index
          </h3>
          <p className="text-xs text-navy-800/60 mt-3">Click any mandal to drill into district-level detail</p>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-navy-800/60 font-numeric">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-600" />&lt; 50</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" />50–75</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-600" />&gt; 75</span>
        </div>
      </div>

      <div className="p-5">
        {isLoading || !data ? (
          <div className="grid grid-cols-6 gap-2">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="h-[68px] bg-cream-100 rounded-sm animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-6 gap-2">
              {data.map((m) => {
                const c = colorClasses(m.color);
                return (
                  <button
                    key={m.code}
                    type="button"
                    onClick={() => onCellClick(m.code)}
                    className={`heatmap-cell ${c.bg} p-2.5 rounded-sm text-left`}
                  >
                    <div className={`text-[10px] uppercase tracking-wide ${c.dim}`}>{m.name}</div>
                    <div className="font-numeric font-semibold text-lg leading-tight">{m.health}</div>
                    <div className={`text-[9px] ${c.dim} font-numeric mt-0.5`}>
                      {m.districts} dist · {m.beneficiaries.toLocaleString('en-IN')} ben
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-cream-100 flex items-center justify-between text-xs">
              <div className="text-navy-800/60">
                <span className="font-medium text-red-700">
                  {data.filter((m) => m.color === 'red').length} mandals
                </span>{' '}
                in red zone need intervention
              </div>
              {data.find((m) => m.color === 'red') && (
                <button
                  type="button"
                  onClick={() => onCellClick(data.find((m) => m.color === 'red')!.code)}
                  className="text-saffron-600 hover:text-saffron-700 font-medium"
                >
                  View red-zone drill-down →
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
